import { useEffect, useRef, useState, type CSSProperties } from "react";

/**
 * Where a sheet cell's dropdown goes, and how tall it may be (#1017).
 *
 * Both spreadsheet pickers — Food's FoodNameCell and Shopping's NameCell — are
 * the same control (#911), so they place their menu the same way. This module
 * is that placement, extracted from FoodNameCell where it was first written, so
 * an edit to either feature cannot drift them apart.
 *
 * Extracted from ListSheet's sibling helpers for the same reason sheetColumns
 * was: the shared part is the BEHAVIOUR, not the vocabulary. Each cell still
 * owns its own suggestions, its own key map and its own idea of what a pick
 * means.
 */

/** Below this the menu is not worth showing in place — roughly two options plus
 * a hint of a third, which is what tells the user it scrolls. It picks the SIDE
 * only; see placeMenu for why it must never become a floor on the height. */
const MIN_MENU_HEIGHT = 132;

const MENU_GAP = 4;

export type MenuPlacement = { top: number; maxHeight: number; flipped: boolean };

/**
 * Where the dropdown goes, and how tall it may be.
 *
 * Pure and exported because every interesting case here was found on a real
 * phone and is miserable to reproduce by hand: a 360×640 device with the IME
 * up, the grid's bottom row, and a landscape viewport of 216px. The unit tests
 * replay those exact measurements.
 *
 * All measurements are in the visual viewport's coordinates — that is the only
 * box that accounts for the on-screen keyboard.
 */
export function placeMenu(input: {
  inputTop: number;
  inputBottom: number;
  viewportTop: number;
  viewportHeight: number;
  /** The menu's natural height; the result is capped to the space available. */
  contentHeight: number;
}): MenuPlacement {
  const { inputTop, inputBottom, viewportTop, viewportHeight, contentHeight } = input;
  const viewportBottom = viewportTop + viewportHeight;
  const spaceBelow = viewportBottom - inputBottom - MENU_GAP;
  const spaceAbove = inputTop - viewportTop - MENU_GAP;

  // Flip only when below is genuinely cramped AND above is roomier: a menu that
  // jumps sides for a few pixels' gain is worse than a short one.
  const flipped = spaceBelow < MIN_MENU_HEIGHT && spaceAbove > spaceBelow;

  // MIN_MENU_HEIGHT chooses the SIDE; it must never become a floor on the
  // height. Measured on the device in landscape (216px of viewport with the IME
  // up): 96px existed below, a floor of 132 was applied, and 36px of menu went
  // under the fold. A short menu that scrolls beats a taller one whose last
  // options cannot be reached.
  const maxHeight = Math.max(0, Math.floor(flipped ? spaceAbove : spaceBelow));
  const height = Math.min(contentHeight, maxHeight);

  // Both directions anchor with `top`, including the flip. Anchoring the flip
  // with `bottom` is the obvious shortcut and it does not work: measured on the
  // device, a position:fixed element with only `bottom` set collapsed to a 10px
  // box while the IME was up — the menu vanished exactly when it was needed.
  const top = flipped
    ? Math.max(viewportTop, inputTop - height - MENU_GAP)
    : inputBottom + MENU_GAP;

  return { top, maxHeight, flipped };
}

/**
 * Has the anchor left the visible viewport entirely, in EITHER axis?
 *
 * The horizontal half is not symmetry for its own sake. These sheets scroll
 * HORIZONTALLY (`.list-sheet-scroll` is `overflow-x: auto`) — that is the whole
 * reason the menu is position:fixed — so the cell the menu hangs off slides
 * sideways out of view while the menu, no longer in the scrolling box, stays
 * put. Measured at 360px with every column shown (702px of horizontal scroll):
 * at scrollLeft 200 the input was already fully off the left edge, and by 450
 * the menu sat entirely off-canvas with 0px visible and still open.
 *
 * A vertical-only check misses all of that, and re-placing against a
 * far-negative `rect.left` is worse than not moving at all.
 */
export function anchorOutsideViewport(input: {
  inputTop: number;
  inputBottom: number;
  inputLeft: number;
  inputRight: number;
  viewportTop: number;
  viewportHeight: number;
  viewportLeft: number;
  viewportWidth: number;
}): boolean {
  const { inputTop, inputBottom, inputLeft, inputRight, viewportTop, viewportHeight, viewportLeft, viewportWidth } = input;
  return (
    inputBottom < viewportTop ||
    inputTop > viewportTop + viewportHeight ||
    inputRight < viewportLeft ||
    inputLeft > viewportLeft + viewportWidth
  );
}

/**
 * Keeps a still-anchored menu on screen horizontally.
 *
 * Between "lined up with the cell" and "half off the edge", the cell wins only
 * while it is fully visible. Once the sheet's horizontal scroll has dragged the
 * anchor PARTWAY off — the cell is still there, so the menu must not be
 * dismissed — aligning to `rect.left` would hang the menu off the edge. Clamping
 * keeps every option tappable at the cost of a menu no longer flush with its
 * cell, which is the same trade the vertical axis already makes when it flips.
 *
 * A menu wider than the viewport clamps to the left edge and overflows right;
 * the CSS max-width means that should not happen, and starting off-screen-left
 * would be strictly worse if it did.
 */
export function clampMenuLeft(input: {
  inputLeft: number;
  menuWidth: number;
  viewportLeft: number;
  viewportWidth: number;
}): number {
  const { inputLeft, menuWidth, viewportLeft, viewportWidth } = input;
  const rightmost = viewportLeft + viewportWidth - menuWidth;
  return Math.max(viewportLeft, Math.min(inputLeft, rightmost));
}

/**
 * The width the menu is ABOUT TO render at, which is not the width it has now.
 *
 * The placement sets `left` and `min-width` in one style update, so measuring
 * `offsetWidth` first reads the box BEFORE that min-width applies. On a first
 * placement Food measured the stylesheet's 240px floor and then painted at the
 * hook's 260px one; clamping against 240 near the right edge left 20px of menu
 * off the edge, and nothing re-placed it until the next scroll or resize
 * (codex review).
 *
 * `min-width` can only ever grow the box — it wins over `max-width` per spec —
 * so the larger of the two is what gets painted. Erring wide is the safe
 * direction: it clamps further from the edge, never past it.
 */
export function renderedMenuWidth(input: {
  measuredWidth: number;
  anchorWidth: number;
  minWidth: number;
}): number {
  return Math.max(input.measuredWidth, input.anchorWidth, input.minWidth);
}

/**
 * The `min-width` the placement may safely ask for.
 *
 * The floor exists so a narrow cell still gets a readable menu, but it must not
 * out-size the screen: pinch-zoom shrinks the VISUAL viewport while the
 * stylesheet keeps sizing in layout-viewport `vw` units, so an uncapped floor
 * paints a box wider than what the user can see. Measured case from review — a
 * 360px layout viewport zoomed to 180px, where Food's 260px floor left 80px of
 * menu off-screen with nowhere to clamp it to.
 *
 * Capping is a no-op at every normal zoom level, where the viewport is far
 * wider than either floor.
 */
export function menuFloorWidth(input: {
  anchorWidth: number;
  minWidth: number;
  viewportWidth: number;
}): number {
  return Math.min(Math.max(input.anchorWidth, input.minWidth), input.viewportWidth);
}

/**
 * Positions a sheet cell's dropdown, and sizes it to the space that actually
 * exists.
 *
 * The sheet scrolls horizontally, and `overflow-x: auto` forces the vertical
 * axis to clip too — an in-flow menu is cut off on the bottom rows, which are
 * exactly the rows these pickers exist for (the blank spare lives there). So it
 * renders position:fixed, measured off the input, which escapes ancestor
 * overflow entirely.
 *
 * The rest is what a real phone forced (measured on an Android device, 360×640
 * with the IME up): a fixed menu happily runs off the bottom of the screen.
 * Eight options at 52px is 327px of menu against 528px of viewport starting at
 * y=398 — 197px of it below the fold, and only TWO options reachable. So:
 *
 *  - `maxHeight` is the space that genuinely remains, measured against
 *    visualViewport, which is the only box that accounts for the keyboard;
 *  - if there is more room above than below, the menu flips above the input,
 *    which is what makes the picker usable on the bottom row of the grid;
 *  - and it re-places on visualViewport resize rather than closing, because the
 *    keyboard appearing IS a resize — closing on it would shut the menu at the
 *    exact moment the user started typing into it.
 *
 * Refs are typed structurally rather than as React's `RefObject` so a caller
 * can pass the ref it already has without caring which React version's ref type
 * `useRef` handed it.
 */
export function useSheetMenuPlacement({
  visible,
  anchorRef,
  containerRef,
  minWidth,
  onDismiss
}: {
  /** The menu is on screen. Placement listeners only exist while this is true. */
  visible: boolean;
  /** The input the menu hangs off; everything is measured from its rect. */
  anchorRef: { current: HTMLElement | null };
  /** The cell wrapper. Scrolls inside it are the menu's own and must not
   * re-place it; scrolls outside it moved the page under the menu. */
  containerRef: { current: HTMLElement | null };
  /** Floor for the menu's width, so a narrow cell still gets a readable menu. */
  minWidth: number;
  /** The anchor scrolled out of the viewport — there is nothing left to hang
   * off, so the caller should close. */
  onDismiss: () => void;
}): { menuRef: { current: HTMLDivElement | null }; menuStyle: CSSProperties | undefined } {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [menuStyle, setMenuStyle] = useState<CSSProperties | undefined>(undefined);
  /** The latest `place`, so the re-measure effect below can call it. */
  const placeRef = useRef<(() => void) | null>(null);
  /** The content box the current placement was computed against. Both axes:
   * the horizontal clamp depends on the width the same way the flip depends on
   * the height, so a width-only change leaves the clamp just as stale. */
  const placedForHeightRef = useRef(-1);
  const placedForWidthRef = useRef(-1);

  // The placement effect must not re-subscribe every render just because the
  // caller passed a fresh closure, so `onDismiss` is read through a ref. This
  // effect is declared FIRST so it runs before the placement effect below on
  // every commit.
  const dismissRef = useRef(onDismiss);
  useEffect(() => {
    dismissRef.current = onDismiss;
  });

  useEffect(() => {
    if (!visible) {
      setMenuStyle(undefined);
      placeRef.current = null;
      placedForHeightRef.current = -1;
      placedForWidthRef.current = -1;
      return undefined;
    }

    const place = () => {
      const rect = anchorRef.current?.getBoundingClientRect();
      if (!rect) return;
      const viewport = window.visualViewport;
      const viewportTop = viewport?.offsetTop ?? 0;
      const viewportHeight = viewport?.height ?? window.innerHeight;
      const viewportLeft = viewport?.offsetLeft ?? 0;
      const viewportWidth = viewport?.width ?? window.innerWidth;

      // The input itself has scrolled out of sight — the page moved under a
      // fixed menu, or the sheet scrolled sideways out from under it. There is
      // nothing left to anchor to, so close.
      if (
        anchorOutsideViewport({
          inputTop: rect.top,
          inputBottom: rect.bottom,
          inputLeft: rect.left,
          inputRight: rect.right,
          viewportTop,
          viewportHeight,
          viewportLeft,
          viewportWidth
        })
      ) {
        dismissRef.current();
        return;
      }

      // `scrollHeight` is the content height regardless of the max-height cap,
      // so one read tells placeMenu how tall the menu wants to be.
      const contentHeight = menuRef.current?.scrollHeight ?? 0;
      placedForHeightRef.current = contentHeight;
      placedForWidthRef.current = menuRef.current?.offsetWidth ?? 0;
      const { top, maxHeight } = placeMenu({
        inputTop: rect.top,
        inputBottom: rect.bottom,
        viewportTop,
        viewportHeight,
        contentHeight
      });

      // Clamp against the width this very update will paint, not the one
      // currently on screen — the same style carries the min-width — and never
      // against more width than the viewport actually has.
      const appliedMinWidth = menuFloorWidth({ anchorWidth: rect.width, minWidth, viewportWidth });
      const paintedWidth = Math.min(
        renderedMenuWidth({
          measuredWidth: menuRef.current?.offsetWidth ?? 0,
          anchorWidth: rect.width,
          minWidth: appliedMinWidth
        }),
        viewportWidth
      );

      // The stylesheet sizes this menu in LAYOUT-viewport units (`80vw`,
      // `92vw`), which pinch-zoom does not shrink — so a zoomed-in user can be
      // handed a menu wider than the screen, with no `left` that fits it.
      // Capping is scoped to that case ONLY: an inline max-width REPLACES the
      // stylesheet's `min(360px, 92vw)` in the cascade, so applying it
      // unconditionally would let the desktop menu grow past its 360px limit.
      const layoutWidth = document.documentElement?.clientWidth ?? viewportWidth;
      const pinchZoomed = viewportWidth < layoutWidth;

      setMenuStyle({
        position: "fixed",
        top,
        left: clampMenuLeft({ inputLeft: rect.left, menuWidth: paintedWidth, viewportLeft, viewportWidth }),
        minWidth: appliedMinWidth,
        maxHeight,
        ...(pinchZoomed ? { maxWidth: viewportWidth } : null)
      });
    };

    placeRef.current = place;
    place();
    const replaceOnOutsideScroll = (event: Event) => {
      if (event.target instanceof Node && containerRef.current?.contains(event.target)) return;
      place();
    };
    window.addEventListener("scroll", replaceOnOutsideScroll, true);
    window.addEventListener("resize", place);
    window.visualViewport?.addEventListener("resize", place);
    window.visualViewport?.addEventListener("scroll", place);
    return () => {
      placeRef.current = null;
      window.removeEventListener("scroll", replaceOnOutsideScroll, true);
      window.removeEventListener("resize", place);
      window.visualViewport?.removeEventListener("resize", place);
      window.visualViewport?.removeEventListener("scroll", place);
    };
    // anchorRef/containerRef are stable ref objects; minWidth is a constant per
    // call site. `visible` is what actually starts and stops the listeners.
  }, [anchorRef, containerRef, minWidth, visible]);

  /**
   * Re-place whenever the menu's own content changed size.
   *
   * A flipped menu is anchored by `top`, computed from how tall the content was
   * at the time — so content that arrives LATER pushes the bottom off the
   * screen while the top stays put. Measured on the Food picker, whose
   * suggestions come from an async catalog search: the menu was placed while it
   * held two options, grew to eight, and 294px of it ended up below the fold
   * with 2 of 8 reachable — the exact defect the placement exists to prevent.
   * Shopping reaches the same state by widening a query (typing "ol" then
   * deleting back to "o" grows the list under a placement made for the short
   * one).
   *
   * WIDTH counts too, not just height (codex review). On desktop the menu is
   * `width: max-content` under a max-width cap, so a result set that changes
   * text without changing the option COUNT resizes it sideways at the same
   * height — and the horizontal clamp then holds a `left` computed for the old
   * width. Watching only the height would leave that stale in exactly the way
   * this effect exists to prevent.
   *
   * Deliberately not a ResizeObserver: this runs after every commit, which is
   * when the content can have changed, and it re-places only when the measured
   * box actually differs — so it cannot loop, and it needs no observer that the
   * headless test page refuses to deliver.
   */
  useEffect(() => {
    if (!visible || !placeRef.current) return;
    const contentHeight = menuRef.current?.scrollHeight ?? 0;
    const contentWidth = menuRef.current?.offsetWidth ?? 0;
    if (contentHeight === placedForHeightRef.current && contentWidth === placedForWidthRef.current) return;
    placeRef.current();
  });

  return { menuRef, menuStyle };
}
